<template>
  <v-app id="app" class="application_style">
    <div @mouseover="handleMouseOver" @mouseleave="handleMouseLeave">
      <v-navigation-drawer
        ref="navigationBar"
        v-model="drawer"
        v-if="!$route.meta.hideMainSidebar && smAndUpVal && !isMaintenanceMode"
        app
        :mini-variant-width="88"
        :mini-variant.sync="sidebarMini"
        :width="sidebarMini ? 88 : 325"
        :permanent="(isPermanent && !drawer) || smAndUpVal"
        :temporary="isPermanent && drawer"
        id="navigation-bar"
        :data-test="role == 'admin' ? 'adminSidebarItem' : 'sidebarItem'"
        class="side-menu-nav"
      >
        <v-sheet @click="sideToggleMenu" class="fill-height">
          <SideBar
            :sidebarOption="role == 'admin' ? adminSidebarItem : sidebarItem"
            :expandOnhover="true"
            :isMini="sidebarMini"
            @toggleMenu="toggleMenu"
            :secondSidebar="!$route.meta.secondSidebar"
            :isPermanentMini="isPermanentMini"
          ></SideBar>
        </v-sheet>
      </v-navigation-drawer>
    </div>
    <v-main
      data-testid="main-container"
      style="max-height: 100vh; overflow-y: scroll"
      class="d-flex justify-center ex-main-container"
    >
      <!-- Maintenance Screen -->
      <exiq-maintenance-screen
        v-if="isMaintenanceMode"
      ></exiq-maintenance-screen>

      <!-- @updateEmployeeSidebarCount="updateCount" -->
      <EmployeeSideBar
        v-if="shouldShowSidebar && !isMaintenanceMode"
        :sidebarOption="employeeSidebar"
        :employeeLoading="employeeLoading"
      />
      <NotFoundView
        v-if="
          getRouteError.code == 404 &&
          getRouteError.visibility == true &&
          !isMaintenanceMode
        "
      />
      <router-view
        v-if="
          smAndUpVal && getRouteError.visibility == false && !isMaintenanceMode
        "
        :key="$route.path"
      >
      </router-view>

      <ExLoader
        v-if="
          isLoading && getRouteError.visibility == false && !isMaintenanceMode
        "
        text="Loading..."
        :type="loaderType"
      />

      <div
        v-if="$vuetify.breakpoint.xsOnly && !isMaintenanceMode"
        class="d-flex flex-column"
      >
        <div class="mb-4 d-flex justify-center mt-8 mb-8">
          <img src="@/assets/login/logo.svg" />
        </div>
        <div class="desktop-only">Desktop only</div>
        <div class="only-desktop-message">
          Sorry, exiqtive.com only supports access via Desktops. No mobile
          support at this time
        </div>
      </div>
    </v-main>

    <!-- IQ Overlay Modal -->
    <IQOverlay
      v-if="iqOverlayStore.isOpen"
      :modelValue="iqOverlayStore.isOpen"
      :initialPrompt="iqOverlayStore.initialPrompt"
      :type="iqOverlayStore.type"
      :showSuggestions="iqOverlayStore.showSuggestions"
      :roleName="iqOverlayStore.roleName"
      :suggestedResponsibilities="iqOverlayStore.suggestedResponsibilities"
      :useCustomLoader="iqOverlayStore.useCustomLoader"
      :showCheckpointSuggestions="iqOverlayStore.showCheckpointSuggestions"
      :responsibilityName="iqOverlayStore.responsibilityName"
      :suggestedCheckpoints="iqOverlayStore.suggestedCheckpoints"
      @close="iqOverlayStore.closeOverlay"
      @submit="iqOverlayStore.emitSubmit"
      @actionClick="iqOverlayStore.emitActionClick"
      @close-navigator="iqOverlayStore.closeNavigator"
      @update:suggestions="iqOverlayStore.updateSuggestions"
      @create-and-attach="iqOverlayStore.emitCreateAndAttach"
      @reject="iqOverlayStore.emitReject"
      @suggest-more="iqOverlayStore.emitSuggestMore"
      @edit="iqOverlayStore.emitEdit"
      @close-checkpoint-navigator="iqOverlayStore.closeCheckpointNavigator"
      @update:checkpoint-suggestions="
        iqOverlayStore.updateCheckpointSuggestions
      "
    />

    <ToastContainer />
    <!-- No Activity Dialog -->
    <NoActivityDialog
      :show="showIdleWarning && isUserLoggedIn"
      @onStayLoggedIn="handleStayLoggedIn"
      @onLogout="handleLogout"
    />
  </v-app>
</template>

<script>
import SideBar from "@components/common/SideBar.vue";
import EmployeeSideBar from "@components/common/EmployeeSideBar.vue";
import ExLoader from "@components/common/ExLoader.vue";
import NotFoundView from "@/views/NotFound.vue";
import IQOverlay from "@components/IQOverlay/IQOverlay.vue";
import ToastContainer from "@components/ConfirmationToast/ToastContainer.vue";
import NoActivityDialog from "@components/NoActivityDialog/NoActivityDialog.vue";
import { getKnock } from "@/plugins/knock";
import "vue-loading-overlay/dist/vue-loading.css";
import { createNamespacedHelpers } from "vuex";
import { getEmployeeChatter, getEmployeeBadges } from "@/api";
import { updateAccountCountsOnLogin } from "@/utils/accountCountUtils";
import { usePositionStore } from "@/store/pinia/position";
import { useEmployeeStore } from "@/store/pinia/employee";
import { useSidebarCountStore } from "@/store/pinia/sidebarCount";
import { useIQOverlayStore } from "@/store/pinia/iqOverlay";
import * as Sentry from "@sentry/vue";
const { mapState: mapDataState } = createNamespacedHelpers("isPosition");
export default {
  name: "App",
  components: {
    SideBar,
    ExLoader,
    EmployeeSideBar,
    NotFoundView,
    IQOverlay,
    ToastContainer,
    NoActivityDialog,
  },
  data: () => ({
    currentAccountDetails: {},
    isHovered: false,
    drawer: false,
    sidebarMini: true,
    toggleMenuClicked: false,
    secondEmpBar: false,
    loader: "spinner",
    isOtherSidebarPresent: false,
    positionStore: usePositionStore(),
    employeeStore: useEmployeeStore(),
    sidebarCountStore: useSidebarCountStore(),
    iqOverlayStore: useIQOverlayStore(),
    isSidebarCollapsedPermanently: false, // Set to false to make it open by default
    role: "",
    showPositionHistoryTab: false,
    showReadinessOrganizationTab: false,
    showNewOrganizationTab: false,
    showNewYourStuffTab: false,
    showNewReadinessTab: false,
    adminSidebarItem: {
      type: "main",
      tItems: [
        {
          name: "ClientLogo",
          img: "clientlogo.svg",
          nameImg: "clientlogoname.svg",
          to: "",
        },
        {
          name: "Home",
          img: "home-01.svg",
          to: "/",
        },
        {
          name: "Accounts",
          img: "Company with Employees-grey.svg",
          to: "",
          count: "10",
        },
        {
          name: "Products",
          img: "box.svg",
          to: "",
          count: "10",
        },
        {
          name: "Subscriptions",
          img: "Subscription-grey.svg",
          to: "",
          count: "10",
        },
        {
          name: "Users",
          img: "users-01.svg",
          to: "",
          count: "10",
        },
        {
          name: "Partners",
          img: "Partnership-grey.svg",
          to: "",
          count: "10",
        },
      ],
      bItems: [
        {
          name: "Support",
          img: "life-buoy.svg",
          to: "",
        },
        {
          name: "Settings",
          img: "settings.svg",
          to: "",
        },
      ],
      cardItem: [
        {
          name: "Olivia Rhye",
          email: "olivia@untitledui.com",
          img: "Avatar.svg",
          to: "/login",
        },
      ],
    },
    idleTime: null,
    idleWarningTime: null,
    showIdleWarning: false,
    employeesData: [],
    employeeLoading: false,
    totalChatterCount: 0,
    activeAssignments: 0,
    currentReadiness: 0,
    accessPermissionsValue: null,
  }),
  computed: {
    getRouteError() {
      return this.$store.state.routeErrorHandling.error;
    },
    isMaintenanceMode() {
      return this.$store.state.isLoading.maintenanceMode;
    },
    isLoading() {
      return this.$store.state.isLoading.isLoading;
    },
    loaderType() {
      return this.$store.state.isLoading.loaderType;
    },
    storePositions() {
      return this.$store.getters["positionStore/positions"] || [];
    },
    currentEmployeeReadiness() {
      return this.$store.getters["employeeStore/currentEmployeeReadiness"] ?? 0;
    },
    currentEmployeeBadges() {
      return this.$store.getters["employeeStore/currentEmployeeBadges"];
    },
    ...mapDataState(["activePositionsCount"]),
    sidebarItem() {
      const currentAccount = this.accessPermissionsValue;
      const val = {
        type: "main",
        tItems: [
          {
            name: "ClientLogo",
            img: "home-01.svg",
            nameImg: "new-logo.svg",
            to: "",
          },
        ],
        bItems: [],
        cardItem: [],
      };
      const readinessItem = {
        name: "Readiness",
        img: "Readiness.svg",
        to: "",
        children: [
          {
            name: "Employees",
            img: "",
            to: "/readiness/employees",
            count: this.sidebarCountStore.employees,
          },
          // commented for v2.1 in ER-3450
          // conditionally show Organization based on feature flag
          ...(this.showReadinessOrganizationTab
            ? [
                {
                  name: "Organization",
                  img: "",
                  to: "/readiness/organization",
                  count: this.sidebarCountStore.positions,
                },
              ]
            : []),
          // {
          //   name: "Time to Readiness",
          //   img: "",
          //   to: "",
          // },
          // commented for v2.1 in ER-3450
          {
            name: "Catalogs",
            img: "",
            to: this.flags["catalogs.dashboard.enabled"]
              ? "/readiness/catalogs"
              : "/readiness/catalogs/roles",
          },
        ],
      };
      const newReadinessItem = {
        name: "New Readiness",
        img: "Readiness.svg",
        to: "/new-readiness",
      };
      const knowledgeItem = {
        name: "Knowledge",
        img: "Knowledge.svg",
        to: "",
        children: [
          {
            name: "Knowledge Catalog",
            img: "",
            to: "",
            count: 10,
          },
          {
            name: "Learning Catalog",
            img: "",
            to: "",
          },
        ],
      };
      const organizationItem = {
        name: "Organization",
        img: "Organization-chart.svg",
        to: "",
        children: [
          {
            name: "Employees",
            img: "",
            to: "/organization/employees",
            count: this.sidebarCountStore.employees,
          },
          {
            name: "Positions",
            img: "",
            to: "/organization/positions",
            count: this.sidebarCountStore.positions,
          },
        ],
      };
      const performanceItem = {
        name: "Performance",
        img: "Performance.svg",
        to: "",
        children: [
          {
            name: "Scorecards",
            img: "",
            to: "",
            count: 10,
          },
          {
            name: "Tasks",
            img: "",
            to: "",
          },
          {
            name: "KPIs Catalog",
            img: "",
            to: "",
            count: 10,
          },
        ],
      };
      const compensationItem = {
        name: "Compensation",
        img: "Compensation.svg",
        to: "",
        children: [
          {
            name: "Employee Base Pay",
            img: "",
            to: "",
          },
          {
            name: "Position Pay Ranges",
            img: "",
            to: "",
            count: 10,
          },
          {
            name: "Pay Ranges",
            img: "",
            to: "",
          },
        ],
      };
      const directoriesItem = {
        name: "Directories",
        img: "Directory.svg",
        to: "/directories",
      };
      const settingsItem = {
        name: "Settings",
        img: "settings.svg",
        to: "/settings",
      };

      const newYourStuff = {
        name: "New Your Stuff",
        img: "MyStuff.svg",
        to: "/new-my-stuff",
      };

      const myStuff = {
        name: "Your Stuff",
        img: "MyStuff.svg",
        to: "",
        children: [
          {
            name: "As Employee",
            img: "",
            to: "/my-stuff/employee",
          },
          {
            name: "As a Master",
            img: "",
            to: "/my-stuff/master",
          },
          {
            name: "As a Coach",
            img: "",
            to: "/my-stuff/coach",
          },
        ],
      };

      if (currentAccount && currentAccount?.user) {
        val.cardItem.push({
          name: `${currentAccount?.user?.first_name} ${currentAccount?.user?.last_name}`,
          firstName: currentAccount?.user?.first_name,
          lastName: currentAccount?.user?.last_name,
          email: currentAccount?.user?.email,
          accountName: currentAccount?.account_name,
          img: "",
          to: "/login",
        });
      }

      const isHidden = true; // according to the use case
      if (!isHidden) {
        val.tItems.push(knowledgeItem);
        val.tItems.push(performanceItem);
        val.tItems.push(compensationItem);
      }
      if (currentAccount && currentAccount?.has_employee) {
        val.tItems.push(myStuff);
        if (this.showNewYourStuffTab) {
          val.tItems.push(newYourStuff);
        }
      }

      if (
        currentAccount &&
        (currentAccount?.is_owner || currentAccount?.is_co_owner)
      ) {
        val.tItems.push(organizationItem);
        // Temporary "New Organization" tab for integration
        if (this.showNewOrganizationTab) {
          val.tItems.push({
            name: "New Organization",
            img: "Organization-chart.svg",
            to: "/new-organization/dashboard",
          });
        }
      }

      if (
        (currentAccount && currentAccount?.is_owner) ||
        currentAccount?.is_co_owner ||
        currentAccount?.has_readiness_access
      ) {
        val.tItems.push(readinessItem);
        if (this.showNewReadinessTab) {
          val.tItems.push(newReadinessItem);
        }
      }
      // show it always at the bottom of this stack
      if (
        this.currentAccountDetails?.show_employee_directory ||
        this.currentAccountDetails?.show_responsibilities_directories
      ) {
        val.tItems.push(directoriesItem);
      }
      if (
        (currentAccount && currentAccount?.is_owner) ||
        currentAccount?.is_co_owner
      ) {
        val.bItems.push(settingsItem);
      }

      return val;
    },
    employeeSidebar() {
      const empId = this.$route.params.id;
      localStorage.setItem("emp_id", empId);
      const baseItems = [
        {
          name: "Path to Readiness",
          img: "Readiness.svg",
          to: `/organization/employees/${empId}/pathtoreadiness`,
          count: `${this.currentEmployeeReadiness}%`,
        },
      ];

      // Add Positions History only if feature flag is enabled
      if (this.showPositionHistoryTab) {
        baseItems.push({
          name: "Positions History",
          img: "Assignment.svg",
          to: `/organization/employees/${empId}/assignment`,
          count:
            this.$store.getters[
              "employeeStore/currentPositionHistoryActiveCount"
            ],
        });
      }

      baseItems.push({
        name: "Badges",
        img: "Badge.svg",
        to: `/organization/employees/${empId}/Badges`,
        count: `${this.$store.getters["employeeStore/currentEmployeeTotalBadges"]}`,
      });

      const val = {
        type: "secondary",
        tItems: baseItems
          .filter((item) => item.name !== "Chatter")
          .concat({
            name: "Chatter",
            img: "Chatter.svg",
            to: `/organization/employees/${empId}/chatter`,
            count:
              this.$store.getters["employeeStore/currentEmployeeChatterCount"],
          }),
        cardItem: [
          {
            firstName: this.employeesData ? this.employeesData.first_name : "",
            lastName: this.employeesData ? this.employeesData.last_name : "",
            position: this.employeesData ? this.employeesData.title : "",
            email: "",
            photoUrl: this.employeesData ? this.employeesData.avatar : "",
            showAvailability: false,
            to: "/organization/employees",
            access: "has-access",
            status: this.employeesData ? this.employeesData.current_status : "",
            archived: this.employeesData ? this.employeesData.archived : "",
          },
        ],
        empData: this.employeesData,
      };
      return val;
    },
    smAndUpVal() {
      return this.$vuetify.breakpoint.smAndUp;
    },
    lgAndUpVal() {
      return this.$vuetify.breakpoint.lgAndUp;
    },
    isPermanent() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    // A new computed property to pass to SideBar to control icon display
    isPermanentMini() {
      return (
        this.isSidebarCollapsedPermanently && !this.$route.meta.secondSidebar
      );
    },
    shouldShowSidebar() {
      const shouldShow =
        this.$route.meta.secondSidebar === "employee" &&
        !this.$route.meta.hideSidebar?.() &&
        this.$store.getters["featureFlags/showEmployeeSidebar"];
      return shouldShow;
    },
    isUserLoggedIn() {
      const token = localStorage.getItem("token");
      const currentAccountID = localStorage.getItem("currentAccountID");
      const isLoginPage = this.$route.path === "/login";
      return !!(token && currentAccountID) && !isLoginPage;
    },
  },
  async mounted() {
    // Initialize LaunchDarkly context on app start
    await this.initializeLaunchDarklyContext();

    this.role = this.$route.meta.role;
    const localStorageToken = localStorage.getItem("token");
    await this.getAccountCount();

    // Watch the employee page horizontal menu flag reactively
    await this.$watchFlag("employee.page_horizontal_menu.enabled", false);
    // Watch the employee position history view flag
    await this.$watchFlag("employee-position-history-view", false);
    // Watch the catalogs dashboard flag
    await this.$watchFlag("catalogs.dashboard.enabled", false);
    // Watch the idea-MDP-402 flag
    await this.$watchFlag("idea-MDP-402", false);
    // Watch the idea-MDP-378 flag
    await this.$watchFlag("idea-MDP-378", false);

    // Watch the idea-MDP-381 flag
    await this.$watchFlag("idea-MDP-381", false);
    // Watch the idea-MDP-385 flag
    await this.$watchFlag("idea-MDP-385", false);
    // Watch new Your Stuff sidebar item flag
    await this.$watchFlag("my-stuff.new-your-stuff.enabled", false);
    // Watch new Readiness sidebar item flag
    await this.$watchFlag("new-readiness", false);
    // Initialize the position history feature flag
    this.showPositionHistoryTab = await this.$getFlag(
      "employee-position-history-view",
      false
    );
    this.showReadinessOrganizationTab = await this.$getFlag(
      "hide_organizational_readiness",
      false
    );
    // Initialize the idea-MDP-385 flag
    this.showNewOrganizationTab = await this.$getFlag("idea-MDP-385", false);
    // Initialize the new Your Stuff sidebar item flag
    this.showNewYourStuffTab = await this.$getFlag(
      "my-stuff.new-your-stuff.enabled",
      false
    );
    // Initialize the new Readiness sidebar item flag
    this.showNewReadinessTab = await this.$getFlag("new-readiness", false);
    const ideaMDP389Flag = await this.$getFlag("idea-MDP-389", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_389", ideaMDP389Flag);
    // Initialize the idea-MDP-378 flag
    const ideaMDP378Flag = await this.$getFlag("idea-MDP-378", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_378", ideaMDP378Flag);
    // Initialize the idea-MDP-381 flag
    const ideaMDP381Flag = await this.$getFlag("idea-MDP-381", false);
    this.$store.commit("featureFlags/SET_IDEA_MDP_381", ideaMDP381Flag);
    if (
      !this.showPositionHistoryTab &&
      this.$route.name === "EmployeeAssignments" &&
      this.$route.params?.id
    ) {
      this.$router.push({
        name: "EmployeeBadges",
        params: { id: this.$route?.params?.id },
      });
    }

    // Initialize feature flags
    await this.$store.dispatch("featureFlags/checkFeatureFlags", this.$route);

    this.$root.$on("accessPermissionUpdated", (data) => {
      this.accessPermissionsValue = data;
    });

    this.$root.$on("currentAccountDetailsUpdated", (data) => {
      this.currentAccountDetails = data;
    });

    // Listen for login event to refresh LaunchDarkly context and flags
    this.$root.$on("userLoggedIn", async () => {
      await this.refreshFlagsAfterLogin();
    });
    if (this.$route.params) {
      const empId = this.$route.params.id;
      if (empId && localStorageToken) {
        await this.getEmployees(empId);
      }
    }
    this.currentAccountDetails = JSON.parse(
      localStorage.getItem("currentAccountDetails")
    );
    this.startIdleTimer();
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
    this.$root.$off("accessPermissionUpdated");
    this.$root.$off("currentAccountDetailsUpdated");
    this.$root.$off("userLoggedIn");
    // Clean up timers
    clearTimeout(this.idleTime);
    clearTimeout(this.idleWarningTime);
    // Remove idle timer event listeners
    window.removeEventListener("load", this.resetTimer, true);
    window.removeEventListener("mousemove", this.resetTimer, true);
    window.removeEventListener("mousedown", this.resetTimer, true);
    window.removeEventListener("click", this.resetTimer, true);
    window.removeEventListener("keydown", this.resetTimer, true);
    window.removeEventListener("scroll", this.resetTimer, true);
  },
  watch: {
    drawer(val) {
      if (val && this.isPermanent) {
        document.addEventListener("click", this.handleClickOutside);
      } else {
        document.removeEventListener("click", this.handleClickOutside);
      }
    },
    $route: {
      immediate: true,
      async handler(newVal) {
        let secondSidebar = newVal.meta.secondSidebar;

        this.secondEmpBar = !!secondSidebar;
        this.isOtherSidebarPresent = this.$route.meta.sideBarPresent;
        this.role = this.$route.meta.role;
        this.accessPermissionsValue = JSON.parse(
          localStorage.getItem("accessPermission")
        );

        if (secondSidebar === "employee") {
          this.sidebarMini = true;
          this.drawer = false;
        } else {
          this.sidebarMini = this.isSidebarCollapsedPermanently; // Use the permanent state for main sidebar
        }

        // Check feature flags for employee detail pages
        if (newVal.meta.secondSidebar === "employee") {
          await this.$store.dispatch("featureFlags/checkFeatureFlags", newVal);
        } else {
          // Update sidebar visibility for other routes
          this.$store.dispatch("featureFlags/updateSidebarVisibility", newVal);
        }
      },
      deep: true,
    },
    "$vuetify.breakpoint.width"() {
      if (this.$route.meta.secondSidebar == "employee") {
        this.sidebarMini = true;
        if (this.sidebarMini) {
          this.drawer = true;
        }
        this.secondEmpBar = false;
      } else {
        this.sidebarMini = this.isSidebarCollapsedPermanently; // Ensure default is collapsed unless explicitly expanded
        if (this.sidebarMini) {
          this.drawer = false;
        }
        this.secondEmpBar = false;
      }
    },
    storePositions() {
      this.updateActiveAssignments();
    },
    "$route.params.id"(newId) {
      if (newId) {
        this.getEmployees(newId);
        this.updateActiveAssignments();
      }
    },
    // Watch for changes in the employee page horizontal menu flag
    'flags["employee.page_horizontal_menu.enabled"]': {
      handler() {
        this.$store.dispatch("featureFlags/checkFeatureFlags", this.$route);
      },
      immediate: true,
    },
    // Watch for changes in the employee position history view flag
    'flags["employee-position-history-view"]': {
      handler(newValue) {
        this.showPositionHistoryTab = newValue;
      },
      immediate: true,
    },
    // Watch for changes in the idea-MDP-402 flag
    'flags["idea-MDP-402"]': {
      handler(newValue) {
        this.$store.commit("featureFlags/SET_IDEA_MDP_402", newValue);
      },
      immediate: true,
    },
    // Watch for changes in the idea-MDP-378 flag
    'flags["idea-MDP-378"]': {
      handler(newValue) {
        this.$store.commit("featureFlags/SET_IDEA_MDP_378", newValue);
      },
      immediate: true,
    },
    // Watch for changes in the idea-MDP-381 flag
    'flags["idea-MDP-381"]': {
      handler(newValue) {
        this.$store.commit("featureFlags/SET_IDEA_MDP_381", newValue);
      },
      immediate: true,
    },
    // Watch for changes in the idea-MDP-385 flag
    'flags["idea-MDP-385"]': {
      handler(newValue) {
        this.showNewOrganizationTab = newValue;
      },
      immediate: true,
    },
    // Watch for changes in the new Your Stuff sidebar item flag
    [`flags["my-stuff.new-your-stuff.enabled"]`]: {
      handler(newValue) {
        this.showNewYourStuffTab = newValue;
      },
      immediate: true,
    },
    'flags["new-readiness"]': {
      handler(newValue) {
        this.showNewReadinessTab = newValue;
      },
      immediate: true,
    },
  },
  methods: {
    async getAccountCount() {
      try {
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        if (accountID) {
          await updateAccountCountsOnLogin(accountID);
        }
      } catch (error) {
        console.error("Error fetching account counts:", error);
      }
    },
    async initializeLaunchDarklyContext() {
      try {
        // Check if user is logged in and has selected an account
        const token = localStorage.getItem("token");
        const userStr = localStorage.getItem("user");
        const accountId = localStorage.getItem("currentAccountID");
        const accessPermissions = localStorage.getItem("accessPermission");

        if (!token || !userStr || !accountId || !accessPermissions) {
          return; // No valid session, use anonymous context
        }

        const user = JSON.parse(userStr);
        const permissions = JSON.parse(accessPermissions);

        // Create account object from stored data
        const account = {
          id: parseInt(accountId),
          name: permissions.account_name || `Account ${accountId}`,
          owner_email: permissions.owner_email || user?.email,
        };

        // Update LaunchDarkly context
        await this.$identifyTenant(user, account);
      } catch (error) {
        console.error("Error initializing LaunchDarkly context:", error);
      }
    },
    async refreshFlagsAfterLogin() {
      try {
        // Re-initialize LaunchDarkly context with authenticated user
        await this.initializeLaunchDarklyContext();

        // Refresh all watched flags to get updated values for the new context
        await this.$refreshFlags();

        // Re-fetch specific flag values that are used directly (not via watchers)
        this.showPositionHistoryTab = await this.$getFlag(
          "employee-position-history-view",
          false
        );
        this.showReadinessOrganizationTab = await this.$getFlag(
          "hide_organizational_readiness",
          false
        );
        this.showNewOrganizationTab = await this.$getFlag(
          "idea-MDP-385",
          false
        );
        this.showNewYourStuffTab = await this.$getFlag(
          "my-stuff.new-your-stuff.enabled",
          false
        );
        this.showNewReadinessTab = await this.$getFlag("new-readiness", false);

        // Update feature flag store
        const ideaMDP389Flag = await this.$getFlag("idea-MDP-389", false);
        this.$store.commit("featureFlags/SET_IDEA_MDP_389", ideaMDP389Flag);
        const ideaMDP378Flag = await this.$getFlag("idea-MDP-378", false);
        this.$store.commit("featureFlags/SET_IDEA_MDP_378", ideaMDP378Flag);
        const ideaMDP381Flag = await this.$getFlag("idea-MDP-381", false);
        this.$store.commit("featureFlags/SET_IDEA_MDP_381", ideaMDP381Flag);

        // Re-check feature flags for the current route
        await this.$store.dispatch(
          "featureFlags/checkFeatureFlags",
          this.$route
        );
      } catch (error) {
        console.error("Error refreshing flags after login:", error);
      }
    },
    handleMouseOver() {
      if (this.$route.meta.secondSidebar) {
        // Employee sidebar hover logic remains the same
        if (this.sidebarMini) {
          this.sidebarMini = false;
        }
      } else {
        // Main sidebar hover logic
        if (
          this.isSidebarCollapsedPermanently &&
          !this.isPermanent &&
          !this.toggleMenuClicked
        ) {
          this.sidebarMini = false; // Temporarily expand on hover if permanently collapsed and not on small screen
        }
        this.isHovered = true; // Keep for other potential hover effects
      }
    },
    handleMouseLeave() {
      if (this.$route.meta.secondSidebar) {
        // Employee sidebar hover logic remains the same
        if (!this.sidebarMini) {
          this.sidebarMini = true;
        }
      } else {
        // Main sidebar hover logic
        if (
          this.isSidebarCollapsedPermanently &&
          !this.isPermanent &&
          !this.toggleMenuClicked
        ) {
          this.sidebarMini = true; // Revert to mini on mouse leave if permanently collapsed
        }
        this.isHovered = false; // Keep for other potential hover effects
      }
    },
    getEmployeesChatter(accountID, empId) {
      getEmployeeChatter(accountID, empId, 1)
        .then(({ data }) => {
          if (data && data.results) {
            const now = new Date();
            const past48Hours = new Date(now.getTime() - 48 * 60 * 60 * 1000);

            const recentCount = data.results.filter((item) => {
              const createdOn = new Date(item.created_on);
              return createdOn >= past48Hours;
            }).length;
            this.totalChatterCount = recentCount;
          }
        })
        .catch((error) => {
          console.error("Error fetching chatter:", error);
        });
    },
    getEmployeesBadges(accountID, empId) {
      getEmployeeBadges(accountID, empId)
        .then(({ data }) => {
          this.$store.dispatch("employeeStore/currentEmployeeBadges", data);
        })
        .catch((error) => {
          console.error("Error fetching badges:", error);
        });
    },
    async getEmployees(empId) {
      // Use centralized employee fetching from Pinia store
      // This prevents duplicate API calls when EmployeeSideBar also fetches the same data
      try {
        this.employeeLoading = true;
        const employeeData = await this.employeeStore.fetchCurrentEmployee(
          empId
        );
        if (employeeData) {
          // Update Vuex store for backwards compatibility
          this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
          this.currentReadiness = employeeData?.primary_readiness;
          this.employeesData = employeeData;
        }
      } catch (error) {
        console.error("Error fetching employee data in App.vue:", error);
      } finally {
        this.employeeLoading = false;
      }
    },
    toggleMenu() {
      if (!this.$route.meta.secondSidebar) {
        // For main sidebar, toggle the permanent collapse state
        this.isSidebarCollapsedPermanently =
          !this.isSidebarCollapsedPermanently;
        this.sidebarMini = this.isSidebarCollapsedPermanently; // Update sidebarMini based on permanent state

        if (this.isPermanent) {
          this.drawer = !this.isSidebarCollapsedPermanently; // Drawer is open if not permanently collapsed
        }

        this.toggleMenuClicked = true;
        setTimeout(() => {
          this.toggleMenuClicked = false;
        }, 300); // Increased timeout duration
      } else {
        // Employee sidebar logic remains the same
        if (!this.isPermanent) {
          this.sidebarMini = !this.sidebarMini;
        } else {
          this.toggleMenuClicked = true;
          this.sidebarMini = !this.sidebarMini;
          if (this.sidebarMini === false) {
            this.drawer = true;
          } else {
            this.drawer = false;
          }
        }
        setTimeout(() => {
          this.$nextTick(() => {
            this.toggleMenuClicked = false;
          });
        }, 100);
      }
    },
    sideToggleMenu() {
      if (this.isPermanent && !this.drawer && !this.toggleMenuClicked) {
        // Only open the drawer if the sidebar is permanently collapsed
        if (this.isSidebarCollapsedPermanently) {
          this.drawer = true;
          this.sidebarMini = false; // Temporarily expand when drawer is open
        }
      }
    },
    handleOutsideClick(event) {
      if (this.isPermanent && this.drawer) {
        const navBarComponent = this.$refs.navigationBar;
        if (navBarComponent) {
          const navBarEl = navBarComponent.$el || navBarComponent;
          if (
            navBarEl &&
            navBarEl.contains &&
            !navBarEl.contains(event.target)
          ) {
            this.drawer = false;
            // Revert to permanently collapsed state when clicking outside
            this.sidebarMini = this.isSidebarCollapsedPermanently;
          }
        }
      }
    },
    startIdleTimer() {
      window.addEventListener("load", this.resetTimer, true);
      window.addEventListener("mousemove", this.resetTimer, true);
      window.addEventListener("mousedown", this.resetTimer, true);
      window.addEventListener("click", this.resetTimer, true);
      window.addEventListener("keydown", this.resetTimer, true);
      window.addEventListener("scroll", this.resetTimer, true);
      // Start the initial timers
      this.resetTimer();
    },
    resetTimer() {
      if (this.showIdleWarning) {
        return;
      }

      clearTimeout(this.idleTime);
      clearTimeout(this.idleWarningTime);

      this.showIdleWarning = false;

      this.idleWarningTime = setTimeout(() => {
        // Only show warning if user is still logged in
        if (this.isUserLoggedIn) {
          this.showIdleWarning = true;
        }
      }, 3300000);

      this.idleTime = setTimeout(this.logout, 3600000);
    },
    stayLoggedIn() {
      this.resetTimer();
    },
    handleStayLoggedIn() {
      this.showIdleWarning = false;
      this.stayLoggedIn();
    },
    handleLogout() {
      this.logout();
    },
    logout() {
      this.showIdleWarning = false;
      if (this.$route.path != "/login") {
        Sentry.setUser(null);
        localStorage.clear();
        this.$resetToAnonymous();

        const knock = getKnock();
        if (knock) {
          knock.feeds.teardownInstances();
          knock.teardown();
        }
        if (this.iqOverlayStore.isOpen) {
          this.iqOverlayStore.closeOverlay();
        }

        this.$router.push({ name: "Login" });
      }
    },
    updateActiveAssignments() {
      // const empId = this.$route.params.id;
      // const positions = [...this.storePositions];
      // this.activeAssignments = positions.filter(
      //   (position) =>
      //     position.status === "active" &&
      //     position.assigned_employees?.some((employee) => employee.id == empId)
      // ).length;
    },
    updateCount() {
      const empId = this.$route.params.id;
      this.getEmployees(empId);
    },
  },
};
</script>
<style>
.v-main__wrap {
  display: flex;
}

.application_style {
  font-family: "Inter" !important;
}

.ex-main-container {
  scrollbar-width: none;
}

.ex-main-container::-webkit-scrollbar {
  display: none;
}

.desktop-only {
  font-size: 36px;
  font-weight: 600;
  line-height: 44px;
  letter-spacing: -0.02em;
  text-align: center;
  color: #475467;
}

.only-desktop-message {
  font-size: 18px;
  font-weight: 400;
  line-height: 28px;
  text-align: center;
  color: #475467;
}

.toggle-button {
  position: absolute;
  right: -13px;
  top: 43px;
  z-index: 9;
}

.plus-icon-small {
  vertical-align: middle;
}

.v-navigation-drawer {
  overflow: visible;
}

.right-side-card {
  position: relative;
  top: 25px;
  left: -15px;
}

.employee-status-side {
  left: 120px;
  top: -10px;
}

.employee-back-icon {
  top: -29px;
}

.employee-three-dots {
  top: -27px;
}

.toggle-button-class {
  display: flex;
  padding: 3.673px;
  justify-content: center;
  align-items: center;
  gap: 5.878px;
  border-radius: 5.878px;
  border: 0.735px solid #7f56d9;
  background: #7f56d9;
  box-shadow: 0px 0.735px 1.469px 0px rgba(16, 24, 40, 0.05);
}

.toggle-button-class:hover {
  background-color: #5b2cc5;
  cursor: url("../assets/cursorArrow.svg"), auto;
}

.el-notification__content {
  font-family: Inter !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  line-height: 20px !important;
  letter-spacing: 0% !important;
  color: #475467 !important;
  text-align: unset !important;
  margin: unset !important;
}

.side-menu-nav {
  transition: width 0.3s ease-in-out !important;
}
</style>
